import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import api from "../../lib/api";

const TEXT_TYPES = { name: "text", text: "text", address: "text", city: "text", state: "text", country: "text", url: "url", email: "email", phone: "tel", whatsapp: "tel", pincode: "text" };

export function PublicFormFields({ form, values, setValues, testidPrefix = "pf" }) {
  const set = (k, v) => setValues({ ...values, [k]: v });
  const base = "w-full h-11 px-4 rounded-xl border border-black/10 bg-white text-sm outline-none focus:ring-2";
  return (
    <div className="space-y-4">
      {form.fields.map((fld) => (
        <div key={fld.key} data-testid={`${testidPrefix}-field-${fld.key}`}>
          <label className="block text-xs font-extrabold mb-1.5" style={{ color: "inherit" }}>
            {fld.label} {fld.required && <span className="text-red-500">*</span>}
          </label>
          {["dropdown"].includes(fld.type) ? (
            <select className={base} value={values[fld.key] ?? fld.default ?? ""} onChange={(e) => set(fld.key, e.target.value)}>
              <option value="">Select…</option>
              {fld.options.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          ) : fld.type === "radio" ? (
            <div className="space-y-1.5">{fld.options.map((o) => (
              <label key={o} className="flex items-center gap-2 text-sm"><input type="radio" name={fld.key} checked={values[fld.key] === o} onChange={() => set(fld.key, o)} className="h-4 w-4" /> {o}</label>
            ))}</div>
          ) : fld.type === "multiselect" ? (
            <div className="space-y-1.5">{fld.options.map((o) => {
              const arr = Array.isArray(values[fld.key]) ? values[fld.key] : [];
              return (
                <label key={o} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={arr.includes(o)} onChange={(e) => set(fld.key, e.target.checked ? [...arr, o] : arr.filter((x) => x !== o))} className="h-4 w-4" /> {o}
                </label>
              );
            })}</div>
          ) : fld.type === "checkbox" ? (
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!values[fld.key]} onChange={(e) => set(fld.key, e.target.checked ? "Yes" : "")} className="h-4 w-4" /> {fld.placeholder || "Yes"}</label>
          ) : fld.type === "textarea" ? (
            <textarea rows={3} className={`${base} h-auto py-3`} placeholder={fld.placeholder} value={values[fld.key] ?? fld.default ?? ""} onChange={(e) => set(fld.key, e.target.value)} />
          ) : fld.type === "number" || fld.type === "currency" ? (
            <input type="number" step="any" className={base} placeholder={fld.placeholder} value={values[fld.key] ?? fld.default ?? ""} onChange={(e) => set(fld.key, e.target.value)} />
          ) : fld.type === "date" ? (
            <input type="date" className={base} value={values[fld.key] ?? ""} onChange={(e) => set(fld.key, e.target.value)} />
          ) : fld.type === "datetime" ? (
            <input type="datetime-local" className={base} value={values[fld.key] ?? ""} onChange={(e) => set(fld.key, e.target.value)} />
          ) : fld.type === "file" ? (
            <input type="file" accept="image/*,application/pdf" className="w-full text-sm"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                if (file.size > 2_000_000) { alert("File too large (max 2MB)"); e.target.value = ""; return; }
                const rd = new FileReader();
                rd.onload = () => set(fld.key, { name: file.name, type: file.type, data: String(rd.result).split(",")[1] });
                rd.readAsDataURL(file);
              }} />
          ) : (
            <input type={TEXT_TYPES[fld.type] || "text"} inputmode={fld.type === "pincode" ? "numeric" : undefined} className={base}
              placeholder={fld.placeholder} value={values[fld.key] ?? fld.default ?? ""} onChange={(e) => set(fld.key, e.target.value)} />
          )}
          {fld.help && <p className="text-[11px] opacity-60 mt-1">{fld.help}</p>}
        </div>
      ))}
    </div>
  );
}

export function PublicFormRenderer({ form, onSubmit, submitting, preview = false }) {
  const [values, setValues] = useState({});
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const d = form.design || {};
  const primary = d.primary_color || "#059669";

  const submit = async (e) => {
    e.preventDefault();
    if (preview) { setDone(true); return; }
    setError("");
    try { await onSubmit(values); setDone(true); }
    catch (err) { setError(err?.response?.data?.detail || "Submission failed. Please try again."); }
  };

  if (done) {
    return (
      <div className="text-center py-10" data-testid="pf-success">
        <CheckCircle2 className="h-12 w-12 mx-auto mb-3" style={{ color: primary }} />
        <p className="font-bold text-lg">{d.success_message || "Thank you!"}</p>
        {preview && <button onClick={() => setDone(false)} className="mt-3 text-xs font-bold underline opacity-60">Reset preview</button>}
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="public-form">
      {error && <p className="text-xs font-bold text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2.5" data-testid="pf-error">{error}</p>}
      <PublicFormFields form={form} values={values} setValues={setValues} />
      <button type="submit" disabled={submitting} data-testid="pf-submit"
        className="w-full h-12 rounded-xl font-bold text-sm text-white flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-60"
        style={{ backgroundColor: primary }}>
        {submitting && <Loader2 className="h-4 w-4 animate-spin" />} {d.button_text || "Submit"}
      </button>
    </form>
  );
}

/* Standalone public page — /forms/:publicId */
export default function FormPublic() {
  const { publicId } = useParams();
  const [form, setForm] = useState(null);
  const [err, setErr] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/public/crm-form/${publicId}`).then((r) => setForm(r.data)).catch(() => setErr("This form is unavailable or unpublished."));
  }, [publicId]);

  useEffect(() => { if (form) document.title = form.design?.title || form.name; }, [form]);

  const onSubmit = async (values) => {
    setSubmitting(true);
    try {
      const utm = Object.fromEntries(new URLSearchParams(window.location.search).entries());
      await api.post(`/public/crm-form/${publicId}/submit`, { values, utm_campaign: utm.utm_campaign || "" });
    } finally { setSubmitting(false); }
  };

  if (err) return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50" data-testid="pf-unavailable">
      <p className="text-sm font-bold text-muted-foreground">{err}</p>
    </div>
  );
  if (!form) return <div className="min-h-screen flex items-center justify-center"><div className="h-10 w-10 rounded-full border-2 border-border border-t-foreground animate-spin" /></div>;

  const d = form.design || {};
  return (
    <div className="min-h-screen py-8 sm:py-12 px-4" style={{ backgroundColor: d.bg || "#f8fafc", color: "#0f172a" }} data-testid="public-form-page">
      <div className={`mx-auto w-full ${d.layout === "card" ? "max-w-md" : "max-w-lg"}`}>
        <div className="bg-white rounded-3xl border border-black/5 shadow-xl p-6 sm:p-8">
          {d.logo && <img src={d.logo} alt="" className="h-10 object-contain mb-4" />}
          <h1 className="font-extrabold text-2xl tracking-tight" style={{ color: d.primary_color || "#059669" }} data-testid="pf-title">{d.title || form.name}</h1>
          {d.description && <p className="text-sm opacity-70 mt-1 mb-5">{d.description}</p>}
          <div className="mt-5">
            <PublicFormRenderer form={form} onSubmit={onSubmit} submitting={submitting} />
          </div>
        </div>
        <p className="text-center text-[10px] mt-4 opacity-40">Powered by MILTROO CRM</p>
      </div>
    </div>
  );
}
